import type { FolderPurpose } from '@/lib/folderPurpose';
import type { Platform } from '../lib/platform';
/**
 * Types mirror the discovery server's response shapes. Keep in sync with
 * server/src/db/schema/* when fields change.
 */

export type LeadPipelineStage = 'new' | 'contacted' | 'replied' | 'converted' | 'dead';

export interface LeadEnrichment {
  username?: string;
  nickname?: string;
  bio?: string;
  email?: string;
  emailType?: 'agency' | 'generic' | null;
  followers?: number;
  following?: number;
  totalLikes?: number;
  videoCount?: number;
  avgViews?: number;
  /** Median (likes+comments+shares)/views over recent videos, as a percentage. */
  engagementRate?: number | null;
  /** Videos that median came from. 0 = not measurable. */
  engagementSample?: number;
  bioLink?: string;
  verified?: boolean;
  /** Creator's country (ISO code), from their posts. Where they post from — not where their audience is. */
  region?: string;
  lastPostedDaysAgo?: number | null;
  profileUrl?: string;
  /** Present on live search results; null = audience never measured. */
  audienceGeo?: AudienceGeo | null;
  [key: string]: unknown;
}

/**
 * Where a creator's audience is, from sampled commenters. **null means not
 * measured** and must never render as "audience elsewhere". Below
 * AUDIENCE_MIN_SAMPLE the shares are too thin to trust.
 */
export interface AudienceGeo {
  /** Distinct commenters counted. */
  sample: number;
  videos: number;
  countries: Record<string, number>;
  languages: Record<string, number>;
  checkedAt: string;
  /** 'tiktok-commenters' (when absent), 'x-repliers-location', 'x-repliers-followers-location', 'x-repliers-verified', 'ig-stats'. */
  method?: string;
  /** People looked at, when more than were located (X). */
  people?: number;
  languageAssisted?: boolean;
}

export const AUDIENCE_MIN_SAMPLE = 20;

export interface GeoCountry {
  code: string;
  name: string;
}

export interface GeoGroup {
  id: string;
  name: string;
  countries: string[];
}

export interface GeoCatalog {
  countries: GeoCountry[];
  groups: GeoGroup[];
}

export interface TermSuggestion {
  term: string;
  source: 'curated' | 'name' | 'ai';
  country: string;
  language?: string;
  /** Results posted from a target country, out of `total`. Absent = not measured. */
  inTarget?: number;
  total?: number;
  share?: number;
}

export interface TermSuggestResult {
  countries: string[];
  baseline: { term: string; inTarget: number; total: number; share: number }[];
  suggestions: TermSuggestion[];
  aiUsed: boolean;
  aiNote?: string;
}

/**
 * Where a creator sits in the outreach funnel, mirrored from
 * spade-outreach-backend onto discovery's creator registry. Ordered
 * least → most progressed.
 */
/** A clipping campaign a folder can be attached to. */
export interface SelectableCampaign {
  id: string;
  name: string;
  status: string;
  description: string | null;
  clipperUrl: string | null;
  niches: string[];
}

/** A machine-proposed search term awaiting approval. */
export interface SearchSuggestion {
  publicId: string;
  term: string;
  searchMode: 'hashtag' | 'sound';
  musicId: string | null;
  challengeId: string | null;
  score: number;
  reach: number | null;
  seedCount: number;
  band: string | null;
  /** What counts as on-topic for this term. Empty = topic filter can't apply. */
  topicTerms: string[];
  source: 'focus_niche' | 'campaign';
  sourceKey: string;
  sourceLabel: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  evidence: Record<string, unknown>;
  createdAt: string;
}

export interface SuggestionListResponse {
  items: SearchSuggestion[];
  countsBySource: { source: string; sourceKey: string; sourceLabel: string; n: number }[];
}

export interface GenerateResult {
  sourcesProcessed: number;
  sourcesSkipped: { label: string; reason: string }[];
  candidatesFound: number;
  created: number;
  campaignsAvailable: boolean;
}

/**
 * A generation pass. It runs for minutes on the server, so the UI starts one
 * and then polls this rather than holding a request open.
 */
export interface GenerationRun {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  result: GenerateResult | null;
  error: string | null;
}

export interface SuggestionFilters {
  minFollowers?: number;
  maxFollowers?: number;
  minAvgViews?: number;
  maxAvgViews?: number;
  maxLastPostedDays?: number;
  minTopicMatch?: number;
  excludeOfficial?: boolean;
  excludeAccountTypes?: string[];
  emailFilter?: 'any' | 'has' | 'none';
  targetCount?: number;
}

export interface SuggestionApproveInput {
  folderId?: string;
  folderName?: string;
  targetCount?: number;
  /** Quality thresholds for the run. Merged onto server defaults. */
  filters?: SuggestionFilters;
  /** Overrides what counts as on-topic. Defaults to the suggestion's own. */
  topicTerms?: string[];
  isRecurring?: boolean;
  recurEveryDays?: number;
}

export type OutreachState =
  | 'none'
  | 'dismissed'
  | 'prospect'
  | 'claimed'
  /** At least one outreacher has DM'd them; nobody has a reply yet. */
  | 'contacted'
  | 'invited'
  | 'joined';

/** What kind of account this is — see the discovery accountClassifier. */
export type AccountType =
  | 'editor'
  | 'clipper'
  | 'fan_page'
  | 'news_gossip'
  | 'brand_official'
  | 'personal'
  | 'unknown';

/** Which search vector put this creator in this folder. */
export type DiscoveryMethod = 'keyword' | 'sound' | 'hashtag' | 'lookalike' | 'csv' | 'manual' | 'repost' | 'similar' | 'location';

/** A hashtag harvested from a folder's own creators, scored as a search term. */
export interface TagCandidate {
  tag: string;
  challengeId: string;
  seedCount: number;
  reach: number;
  score: number;
  band: 'niche' | 'topic' | 'broad';
}

export interface LookalikeFilters {
  minFollowers: number | null;
  maxFollowers: number | null;
  minAvgViews: number | null;
  maxAvgViews: number | null;
  maxLastPostedDays: number | null;
  emailFilter: string;
}

export interface LookalikeAnalysis {
  seedsUsed: string[];
  postsScanned: number;
  hashtagsSeen: number;
  candidates: TagCandidate[];
  excluded: { tag: string; reason: string; reach?: number }[];
  suggested: string[];
  /** The folder's most recent search filters, so the run can inherit them. */
  lastFilters: LookalikeFilters | null;
  folder: { publicId: string; name: string };
  /** Sounds at least two seeds posted with, most shared first. Older servers omit it or send musicId/title/seedCount only. */
  sounds?: LookalikeSound[];
  /** Optional: older discovery builds don't send it. */
  diagnostics?: {
    /** Seeds a tag needed to count as shared — scales with how many were read. */
    minSeedCount?: number;
  };
}

/** A sound several of the folder's creators posted with. */
export interface LookalikeSound {
  musicId: string;
  title: string | null;
  seedCount: number;
  author?: string | null;
  coverUrl?: string | null;
  playUrl?: string | null;
}

/** How many of a folder's creators can seed a lookalike analysis. */
export interface LookalikeSeedCounts {
  /** TikTok creators that aren't official or gossip accounts. */
  eligible: number;
  /** Of those, already invited or joined — always read first. */
  proven: number;
  /** Most seeds one analysis will read. */
  max: number;
  default: number;
}

export interface LookalikeRunInput {
  hashtags: string[];
  createFolder?: boolean;
  folderName?: string;
  targetCount?: number;
  excludeOfficial?: boolean;
  minFollowers?: number;
  maxFollowers?: number;
  minAvgViews?: number;
  maxAvgViews?: number;
  maxLastPostedDays?: number;
  emailFilter?: string;
  /** Keep only person-led accounts, or only faceless ones. */
  presenterFilter?: 'faces' | 'faceless';
}

export interface LookalikeRunResult {
  jobId: string;
  folder: { publicId: string; name: string; isNew: boolean };
}

/** Fields every lead row now carries from its registry creator. */
export interface CreatorOutreachFields {
  /** Absent on rows from before multi-platform — those are TikTok. */
  platform?: Platform;
  accountType: AccountType | null;
  accountTypeConfidence: number | null;
  accountTypeSignals: string[] | null;
  /**
   * Contact-sheet verdict: is a real person fronting this account?
   * `recurring_person` / `multiple_people` are person-led; `varied_footage` is
   * the clip-farm signature. **null means not measured** — the check never ran
   * for this creator — and must never render as "faceless".
   */
  presenter: string | null;
  presenterConfidence: number | null;
  presenterPresentation: string | null;
  presenterFormats: string[] | null;
  presenterReason: string | null;
  /** Where the audience is, from sampled commenters. Null = never measured. */
  audienceGeo: AudienceGeo | null;
  /** Campaign-fit attributes from the same vision call. Null = never captured. */
  presenterProfile: {
    audienceAgeBand?: string | null;
    setting?: string | null;
    productionQuality?: string | null;
    categories?: string[];
    brandSafety?: string[];
  } | null;
  outreachState: OutreachState;
  outreachProspectPublicId: string | null;
  outreachInvitePublicId: string | null;
  outreachClaimedByName: string | null;
  outreachCheckedAt: string | null;
  suppressedAt: string | null;
}

export interface Creator extends CreatorOutreachFields {
  id: number;
  teamId: string;
  username: string;
  usernameLower: string;
  enrichment: LeadEnrichment | null;
  enrichedAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  suppressReason: string | null;
  notes: string | null;
  /** Names of every folder this creator appears in. */
  folderNames: string[];
  folderCount: number;
}

export interface CreatorListResponse {
  creators: Creator[];
  total: number;
  limit: number;
  offset: number;
}

export type OutreachStateCounts = Record<OutreachState, number>;

export interface CreatorListFilters {
  platform?: Platform;
  search?: string;
  outreachState?: OutreachState;
  folderId?: string;
  suppressed?: boolean;
  hasEmail?: boolean;
  minFollowers?: number;
  maxFollowers?: number;
  limit?: number;
  offset?: number;
}

export type { FolderPurpose } from '@/lib/folderPurpose';

export interface Folder {
  id: number;
  publicId: string;
  teamId: string;
  name: string;
  description: string | null;
  /** Absent on older servers — read through `folderPurpose()`, which defaults to clipping. */
  purpose?: FolderPurpose;
  /**
   * On a PATCH that changed the purpose: whether outreach's lead queue has
   * the new value. False means it will catch up on the next export.
   */
  purposeSynced?: boolean;
  createdByUserId: string | null;
  createdByName: string | null;
  color: string | null;
  archivedAt: string | null;
  pinnedAt: string | null;
  /** Set when this folder was produced by a lookalike run off another folder. */
  derivedFromFolderId: number | null;
  createdAt: string;
  updatedAt: string;
  /** Present on the list endpoint; absent on create/update responses. */
  leadCount?: number;
  inOutreachCount?: number;
  untouchedCount?: number;
  /** Lifecycle (contract §3). Absent on older servers — derive from archivedAt. */
  status?: FolderStatus;
  completeSince?: string | null;
  autoArchivedAt?: string | null;
  archivedReason?: 'manual' | 'auto' | null;
  archiveSnoozedUntil?: string | null;
  ownerName?: string | null;
  counts?: FolderCounts;
}

export interface FolderCreateInput {
  name: string;
  description?: string | null;
  color?: string | null;
  purpose?: FolderPurpose;
}

export interface FolderUpdateInput {
  name?: string;
  description?: string | null;
  color?: string | null;
  archived?: boolean;
  pinned?: boolean;
  purpose?: FolderPurpose;
  /** Restore an archived or complete folder (contract §3). */
  status?: 'active';
}

/**
 * Admin briefing shown to outreachers alongside a folder's prospects.
 * Stored outreach-side; discovery proxies reads and writes.
 */
export interface FolderGuidance {
  sourceFolderPublicId: string;
  sourceFolderName: string | null;
  recommendedCampaigns: string | null;
  /** Clipping campaign ids this folder targets. Ids only — resolved on read. */
  campaignIds: string[];
  pitchNotes: string | null;
  updatedByName: string | null;
  updatedAt: string;
}

export interface SearchPreset {
  id: number;
  publicId: string;
  teamId: string;
  name: string;
  description: string | null;
  params: Record<string, unknown>;
  createdByName: string | null;
  useCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Lead extends CreatorOutreachFields {
  id: number;
  teamId: string;
  folderId: number;
  creatorId: number;
  username: string;
  pipeline: LeadPipelineStage;
  discoveryMethod: DiscoveryMethod;
  discoverySource: string | null;
  /** 0-1: fraction of their recent posts on-topic for the search that found them. */
  topicMatch: number | null;
  enrichment: LeadEnrichment | null;
  notes: string | null;
  dmSentAt: string | null;
  savedAt: string;
  updatedAt: string;
  /** Lifecycle + assignment (contract §3). Optional until every server sends them. */
  doneReason?: LeadDoneReason | null;
  irrelevantAt?: string | null;
  completedAt?: string | null;
  assignedTo?: LeadAssignee | null;
  claimedByName?: string | null;
  prospectPublicId?: string | null;
  lastContactedAt?: string | null;
  otherFolders?: FolderRef[];
}

export interface SearchHistoryEntry {
  id: number;
  teamId: string;
  folderId: number;
  params: Record<string, unknown>;
  ranAt: string;
}

export type AccountStatus = 'active' | 'paused' | 'expired';
export type AccountMode = 'browserbase' | 'local';

export interface AccountSummary {
  id: string; // server returns publicId here
  label: string;
  tiktokUsername: string | null;
  status: AccountStatus;
  createdAt: string;
  mode: AccountMode;
  proxyUrl: string | null;
  tiktokEmail: string | null;
  hasCookies: boolean;
  hasPassword: boolean;
  loginSessionId: string | null;
}

export interface AccountCreateInput {
  tiktokUsername?: string;
  tiktokEmail?: string;
  tiktokPassword?: string;
  proxyUrl?: string;
  cookies?: TikTokCookie[];
  liveSessionId?: string;
  mode?: AccountMode;
}

export interface TikTokCookie {
  name: string;
  value: string;
  domain: string;
  path?: string;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: string;
  expirationDate?: number;
  session?: boolean;
}

export type CampaignStatus = 'pending' | 'running' | 'done' | 'stopped' | 'error';

export interface CampaignLogEntry {
  msg: string;
  type: 'info' | 'success' | 'error';
  ts: number;
}

export interface CampaignResultEntry {
  status: 'sent' | 'failed';
  sentAt?: string;
  error?: string;
  message: string;
  nickname: string;
  followers: number;
}

export interface CampaignLive {
  id: string;
  teamId: string;
  folderId: string;
  folderName: string;
  accountId: string;
  accountLabel: string;
  template: string;
  totalLeads: number;
  sent: number;
  failed: number;
  currentIndex: number;
  status: CampaignStatus;
  logs: CampaignLogEntry[];
  results: Record<string, CampaignResultEntry>;
  createdAt: string;
  finishedAt?: string | null;
  sessionId: string | null;
}

export interface CampaignPoll {
  status: CampaignStatus;
  logs: CampaignLogEntry[];
  sent: number;
  failed: number;
  currentIndex: number;
  totalLeads: number;
  results: Record<string, CampaignResultEntry>;
}

export interface CampaignCreateInput {
  folderId: string;
  accountId: string;
  template?: string;
  minDelay?: number;
  maxDelay?: number;
  dailyLimit?: number;
  dmFilter?: 'all' | 'not-sent' | 'failed';
}

export interface ScrapeJobLogEntry {
  type: 'status' | 'progress' | 'lead' | 'done' | 'error';
  msg: string;
  ts: number;
}

/**
 * Why candidates were dropped before we spent an enrich call. Split by reason
 * so "already invited" reads differently from "already in another folder".
 */
export interface SkipBreakdown {
  known?: number;
  outreach?: number;
  joined?: number;
  suppressed?: number;
  /** Wrong kind of account — official, news/gossip. */
  offTarget?: number;
  /** Recent posts weren't about what was searched for. */
  offTopic?: number;
  /** Face-only search: nobody fronts the account. */
  faceless?: number;
  /** Faceless-only search: a real person fronts the account. */
  hasFace?: number;
  /** The on-camera check couldn't reach a verdict either way. */
  unverified?: number;
  /** Audience measured, and too little of it is in the target countries. */
  audienceMiss?: number;
  /** Too few commenters to measure the audience. */
  audienceUnmeasured?: number;
  languageMiss?: number;
  languageUnmeasured?: number;
  countryMiss?: number;
  countryUnmeasured?: number;
  notPage?: number;
  notPerson?: number;
  /** Numeric and profile filters, checked after the profile is fetched. */
  fewFollowers?: number;
  tooManyFollowers?: number;
  lowViews?: number;
  highViews?: number;
  videoCount?: number;
  inactive?: number;
  lowEngagement?: number;
  lowPostRate?: number;
  notVerified?: number;
  noBioLink?: number;
  bioMismatch?: number;
  emailMismatch?: number;
  accountKind?: number;
  profileUnavailable?: number;
}

export interface ScrapeJob {
  id: string;
  status: 'running' | 'done' | 'error';
  logs: ScrapeJobLogEntry[];
  leads: LeadEnrichment[];
  saved: number;
  checked: number;
  cacheHits: number;
  apiCalls: number;
  preFiltered: number;
  skipped: number;
  skipBreakdown: SkipBreakdown | null;
  /** What a running search is doing right now. */
  stage?: string | null;
  startedAt: string;
  finishedAt: string | null;
  folderId: string;
}

export interface ScrapeJobPoll {
  status: 'running' | 'done' | 'error';
  logs: ScrapeJobLogEntry[];
  newLeads: LeadEnrichment[];
  saved: number;
  checked: number;
  cacheHits: number;
  apiCalls: number;
  preFiltered: number;
  skipped: number;
  skipBreakdown: SkipBreakdown | null;
  /** What a running search is doing right now. */
  stage?: string | null;
  /**
   * What loosening a numeric floor would have bought, computed from creators
   * this run already enriched and rejected. Null = no relaxable floor was set;
   * an empty array = a floor was set and loosening it adds nobody.
   */
  widenAnalysis: WidenOption[] | null;
  finishedAt: string | null;
}

export interface WidenOption {
  filter: 'minAvgViews' | 'minFollowers';
  value: number;
  gained: number;
  sample: { username: string; followers: number; avgViews: number }[];
}

export interface JobSummary {
  id: string;
  status: 'running' | 'done' | 'error';
  folderId: string;
  saved: number;
  checked: number;
  apiCalls: number;
  cacheHits: number;
  skipped: number;
  targetCount: number;
  keywords: string;
  searchMode: 'keyword' | 'sound';
  startedAt: string;
  finishedAt: string | null;
}

/**
 * Search modes per platform. TikTok: keyword, sound, hashtag, usernames.
 * Instagram: keyword (accounts), hashtag, sound (reel audio), similar,
 * usernames. X: keyword (posts), people, similar, usernames.
 */
export type SearchMode = 'keyword' | 'sound' | 'hashtag' | 'people' | 'lists' | 'similar' | 'usernames';

export interface ScrapeParams {
  /** Which platform to search. Absent = TikTok. */
  platform?: Platform;
  searchMode?: SearchMode;
  hashtags?: string[];
  usernames?: string[];
  /** Seeds for "similar accounts" (Instagram related profiles, X who-to-follow). */
  seedUsernames?: string[];
  /** X only: which search tab, a language operator, and a minimum like count. */
  xSearchType?: 'Videos' | 'Top' | 'Latest';
  xLang?: string;
  xMinFaves?: number;
  /** X only: confirm repliers' countries with X itself (~20 extra calls per creator). */
  xVerifyAudience?: boolean;
  /** Keep creators whose posts are mostly in these languages (ISO 639-1). Free. */
  contentLanguages?: string[];
  /** 0-1 share of their detectable posts in those languages. Server default 0.5. */
  minLanguageShare?: number;
  /** Keep creators based in these countries (codes or group ids). */
  creatorCountries?: string[];
  /** Instagram only: business / creator / personal. */
  igAccountKinds?: string[];
  /** Median engagement over views, as a percentage. */
  minEngagementRate?: number;
  /** 'pages' = theme / news / meme / clip pages; 'people' = personal accounts. */
  pageFilter?: 'pages' | 'people';
  /** Lifetime posts per day (X). */
  minPostsPerDay?: number;
  /** TikTok keyword: keep finding creators past keyword search's ~6 pages. */
  maxReach?: boolean;
  /** TikTok sound: when the sounds run dry, follow the sounds the kept creators use. */
  soundSnowball?: boolean;
  /** What recent posts must be about. Overrides using the search words as topic words. */
  topicTerms?: string[];
  keywords?: string;
  /** Multiple keyword terms — each gets its own pagination run, deduped. */
  keywordsList?: string[];
  musicId?: string;
  folderId: string;
  region?: string;
  targetCount?: number;
  sortType?: number;
  publishTime?: number;
  minFollowers?: number;
  maxFollowers?: number;
  minAvgViews?: number;
  maxAvgViews?: number;
  minVideoCount?: number;
  maxVideoCount?: number;
  emailFilter?: 'any' | 'none' | 'any-email' | 'generic' | 'agency';
  maxLastPostedDays?: number;
  bioKeywords?: string;
  verifiedOnly?: boolean;
  requireBioLink?: boolean;
  /** Run each keyword with all 3 sort types (relevance, likes, date). */
  rotateSorts?: boolean;
  /** Run each keyword across multiple publish-time windows. */
  rotateTimeWindows?: boolean;
  /** Also search with no region filter (if a region is selected). */
  alsoSearchGlobally?: boolean;
  /** Allow creators already saved in another folder. Never overrides the
   *  outreach/platform/suppressed skips. */
  allowDuplicates?: boolean;
  /** Re-surface creators an outreacher previously dismissed. */
  includeDismissed?: boolean;
  /** Set when the search was launched from a saved preset, so usage ranks it. */
  presetId?: string;
  /** Drop broadcaster / gossip accounts — they beat creators on every numeric filter. */
  excludeOfficial?: boolean;
  /**
   * Only keep creators with a recurring on-camera presenter — the
   * influencer/faceless split. Costs a small per-creator vision call, so the
   * server runs it last, on candidates that already cleared every other
   * filter. No-ops (with a line in the job log) when the discovery server has
   * no Anthropic key; `useCapabilities().presenterCheck` says which.
   */
  faceCreatorsOnly?: boolean;
  /**
   * Which side of the on-camera line to keep: person-led accounts, or clip /
   * edit / repost accounts. Supersedes `faceCreatorsOnly`, which is still sent
   * alongside `'faces'` so an older server applies it rather than ignoring it.
   */
  presenterFilter?: 'faces' | 'faceless';
  /**
   * Keep only creators whose audience is in `audienceCountries` (codes or
   * group ids). Opt-in: countries without this flag filter nothing.
   */
  audienceFilter?: boolean;
  audienceCountries?: string[];
  /** 0-1 share of commenters that must be in those countries. Server default 0.3. */
  minAudienceShare?: number;
  /** One strategy is run per sound. `musicId` remains for saved searches. */
  musicIds?: string[];
  minTopicMatch?: number;
}

export type ProspectStatus = 'AVAILABLE' | 'CLAIMED' | 'DISMISSED' | 'PROMOTED';

export type BulkExportOutcome =
  | { status: 'created'; publicId: string }
  | { status: 'already-prospect'; publicId: string; existingStatus: ProspectStatus }
  | { status: 'already-invited'; invitePublicId: string }
  | { status: 'already-clipping-member' };

export interface BulkExportResponse {
  folderPublicId: string;
  results: Array<{ discoveryLeadId: string; outcome: BulkExportOutcome }>;
}

export interface DedupeMatch {
  inProspect: { publicId: string; status: ProspectStatus } | null;
  inInvite: { publicId: string } | null;
}

export interface DedupeStatusResponse {
  matches: Record<string, DedupeMatch>;
}

export interface OutreachInviter {
  id: number;
  publicId: string;
  name: string;
  team: string;
  email: string | null;
  isActive: boolean;
}

export interface OutreachTeam {
  id: number;
  code: string;
  displayName: string;
  isActive: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** What the discovery server has turned on — see GET /api/capabilities. */
export interface Capabilities {
  outreach: boolean;
  /** Whether the "face creators only" search filter can actually run. */
  presenterCheck: boolean;
  /** Server supports the audience-country filter. Absent on older servers. */
  audienceFilter?: boolean;
  /** Term suggestions include AI translations, not only city/country-name terms. */
  termTranslation?: boolean;
  /** Platforms this server can search. Absent (older server) = TikTok only. */
  platforms?: Platform[];
  /** Server has the Email page's endpoints. Absent on older servers. */
  emailOutreach?: boolean;
  /** An Instantly workspace is connected, so segments can be pushed and tracked. */
  instantly?: boolean;
}

/** A pasted TikTok sound link or id, resolved to something checkable. */
/** One sound the New search form suggests adding. */
export interface SuggestedSound {
  platform: 'tiktok';
  id: string;
  title: string | null;
  reason: string;
  /** Topic list only: how many of the videos read used it. */
  uses?: number;
}

/** POST /api/sounds/suggest. Saved groups are free; `topic` cost `calls` searches. */
export interface SuggestedSoundsResponse {
  groups: { key: 'campaign' | 'proven' | 'lookalike' | 'earlier' | 'topic'; label: string; sounds: SuggestedSound[] }[];
  calls: number;
  topicError: string | null;
}

export interface ResolvedSound {
  musicId: string;
  /** e.g. "original sound - M·A·C Cosmetics". Null if naming it failed. */
  title: string | null;
  author: string | null;
  coverUrl: string | null;
  duration: number | null;
  /** Direct audio URL for quick listen. Absent on older servers. */
  playUrl?: string | null;
  videoCount?: number | null;
}

/** One sound from the catalogue (POST /api/sounds/lookup). Null = not known. */
export interface SoundInfo {
  platform: 'tiktok' | 'instagram';
  id: string;
  title: string | null;
  author: string | null;
  coverUrl: string | null;
  /** Direct audio URL (TikTok only) — what quick listen plays. */
  playUrl: string | null;
  duration: number | null;
  videoCount: number | null;
  /** The sound's page on its platform. */
  url: string;
}

/** POST /api/suggestions/from-sound. */
export interface SoundSuggestionInput {
  platform?: 'tiktok';
  musicId: string;
  title?: string | null;
  /** On-topic words for the search; approval needs some when a topic floor is set. */
  topicTerms?: string[];
  folderPublicId?: string;
  seedCount?: number;
  seedsUsed?: number;
}

export interface SoundSuggestionResult {
  /** False when the sound was already waiting, or decided recently. */
  created: boolean;
  suggestion: { publicId: string; term: string; status: string; topicTerms?: string[] };
}

/** A saved search that re-runs itself into one folder on a cadence. */
export interface SearchSchedule {
  publicId: string;
  name: string;
  everyDays: number;
  enabled: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastJobId: string | null;
  /** Creators the last run added — the number that says whether it still earns. */
  lastAdded: number | null;
  totalAdded: number;
  runCount: number;
  createdByName: string | null;
  /** 'dry' when the server paused it for adding nobody. Absent on older servers. */
  pausedReason?: string | null;
}

// ── Repeat runs: every schedule across folders, and what each run did ────────
// Served by GET /api/schedules, /api/schedules/:id/runs and /api/jobs/:id/added.
// The portal can deploy before the discovery service, so everything here is
// read through src/discovery/repeat-runs/model.ts, which fills in what an
// older server leaves out.

export type ScheduleHealth = 'earning' | 'dry' | 'failing' | 'paused' | 'new';

export interface CountryRef {
  code: string;
  name: string;
}

/** One stored entry of a country list: a country, a group with its members, or something unrecognised. */
export type CountryItem =
  | { kind: 'country'; code: string; name: string }
  | { kind: 'group'; id: string; name: string; count: number; countries: CountryRef[] }
  | { kind: 'unknown'; raw: string };

/** One setting a schedule (or a run) runs with, in words. Built by the server from the search schema. */
export interface ParamSummaryEntry {
  key: string;
  label: string;
  value: string;
  group: string;
  groupLabel: string;
  items?: CountryItem[];
  expanded?: CountryRef[];
  /** The worker ignores this setting as stored (see `note`). */
  inactive?: boolean;
  note?: string;
}

/** One setting that differs between two sets of params. */
export interface ParamChange {
  key: string;
  label: string;
  before: string | null;
  after: string | null;
  countriesAdded?: CountryRef[];
  countriesRemoved?: CountryRef[];
}

/** Added creators by their own country, biggest first. */
export interface CountryCount {
  country: string | null;
  name: string;
  count: number;
  outsideTarget: boolean;
}
export type RunStatus = 'running' | 'done' | 'stopped' | 'failed';

/** One run, as the sparkline draws it. */
export interface RunPoint {
  jobId: string;
  runAt: string;
  added: number;
  status: RunStatus;
}

export interface RepeatSchedule extends SearchSchedule {
  platform: Platform;
  folder: { publicId: string; name: string; color: string | null; archived: boolean } | null;
  /** Paused because its folder was archived; restoring the folder resumes it. */
  pausedByArchive: boolean;
  /**
   * Why the schedule is off, when the server paused it itself: 'dry' = it hit
   * `schedules.autoPauseAfterDryRuns`. Null = paused by a person, or not paused.
   */
  pausedReason: string | null;
  /** How many dry runs in a row triggered the automatic pause. Null = not reported. */
  pausedAfterDryRuns: number | null;
  /** Posts per 100 leads this schedule added. Null = not reported on the row (see /api/insights/outcomes). */
  postsPer100: number | null;
  health: ScheduleHealth;
  /** Consecutive finished runs, newest first, that added nobody. */
  dryRuns: number;
  /** Oldest → newest. Empty when the server doesn't send run history. */
  recentRuns: RunPoint[];
  /** Runs that have a history row (≤ runCount). Null = not reported. */
  runsRecorded: number | null;
  createdAt: string | null;
  /** From when run history is complete; later than createdAt for older schedules. */
  historySince: string | null;
  historyComplete: boolean;
  /** The stored search. Null when the server doesn't send it. */
  params: Record<string, unknown> | null;
  /** The same settings in words. Empty when the server doesn't send it. */
  paramsSummary: ParamSummaryEntry[];
  /** When the settings were last edited; null = still the search it was created with. */
  paramsUpdatedAt: string | null;
  paramsUpdatedByName: string | null;
  /** Creators its runs added from outside the countries those runs filtered on. Null = not reported. */
  outsideTargetTotal: number | null;
}

export interface RepeatRunsSummary {
  total: number;
  active: number;
  paused: number;
  dry: number;
  failing: number;
  /** Null when the server didn't report it and it can't be derived. */
  runsLast7Days: number | null;
  addedLast7Days: number | null;
  addedLast30Days: number | null;
}

export interface RepeatRunsResponse {
  schedules: RepeatSchedule[];
  summary: RepeatRunsSummary;
}

/** One row of a schedule's run history. */
export interface ScheduleRun {
  jobId: string;
  runAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  status: RunStatus;
  stopReason: string | null;
  /** Candidates the run reached a verdict on = the four below. */
  scanned: number | null;
  added: number;
  /** Of `added`, how many are still in the folder. Null = not reported. */
  addedInFolder: number | null;
  /** Skipped because the team already has them. Null = not recorded for this run. */
  skippedKnown: number | null;
  filteredOut: number | null;
  failed: number | null;
  apiCalls: number | null;
  error: string | null;
  /** This run used settings that differ from the schedule's current ones. */
  settingsDiffer: boolean;
  /** Added creators by country. Empty when the server doesn't send it. */
  countries: CountryCount[];
  /** Null = not reported. */
  outsideTargetCount: number | null;
  unknownCountryCount: number | null;
}

export interface ScheduleRunsResponse {
  schedule: RepeatSchedule | null;
  runs: ScheduleRun[];
  page: number;
  pageSize: number;
  total: number;
}

/** A creator one run put in its folder. */
export interface RunAddedCreator extends Pick<CreatorOutreachFields, 'outreachState' | 'outreachClaimedByName' | 'suppressedAt'> {
  username: string;
  platform: Platform;
  nickname: string | null;
  followers: number | null;
  avgViews: number | null;
  savedAt: string | null;
  doneReason: LeadDoneReason | null;
  /** The creator's own country; null = not known. */
  country: CountryRef | null;
  contentLanguage: string | null;
  /** Outside the countries this run filtered on. */
  outsideTarget: boolean;
}

export interface RunAddedResponse {
  jobId: string;
  runAt: string | null;
  /** The run's own count; `total` is how many of them are still in the folder. */
  added: number | null;
  folder: { publicId: string; name: string } | null;
  schedule: { publicId: string; name: string } | null;
  /** ISO codes this run's country filter accepted. Null = not reported; empty = it didn't filter on country. */
  targetCountries: string[] | null;
  outsideTargetCount: number | null;
  countries: CountryCount[];
  /** What this run ran with. */
  paramsSummary: ParamSummaryEntry[];
  settingsDiffer: boolean;
  /** This run's settings → the schedule's current ones. */
  settingsChanges: ParamChange[];
  creators: RunAddedCreator[];
  page: number;
  pageSize: number;
  total: number;
}

/** A creator any run of a schedule put in its folder. */
export interface ScheduleAddedCreator
  extends Pick<CreatorOutreachFields, 'outreachState' | 'outreachClaimedByName' | 'suppressedAt'> {
  leadId: number;
  username: string;
  platform: Platform;
  nickname: string | null;
  country: CountryRef | null;
  contentLanguage: string | null;
  followers: number | null;
  avgViews: number | null;
  email: string | null;
  /** The run that added them. */
  jobId: string;
  runAt: string;
  savedAt: string;
  doneReason: LeadDoneReason | null;
}

/** Every creator a schedule's runs added, newest first. */
export interface ScheduleAddedResponse {
  folder: { publicId: string; name: string };
  creators: ScheduleAddedCreator[];
  page: number;
  pageSize: number;
  total: number;
}

/** Result of pasting handles into a folder — it starts a normal search job. */
export interface AddUsernamesResult {
  jobId: string;
  count: number;
}

export interface RepostCandidate {
  username: string;
  nickname: string;
  /** How many of the folder's creators reposted them. Rarely above 1. */
  seedCount: number;
  /** Total reposts seen across all seeds — what the ordering rests on. */
  repostCount: number;
  topPlays: number;
  viaSeeds: string[];
}

export interface RepostAnalysis {
  seedsUsed: string[];
  seedsFailed: number;
  repostsScanned: number;
  candidates: RepostCandidate[];
  excluded: { username: string; reason: 'self' | 'known' | 'viral' }[];
  suggested: string[];
  folder: { publicId: string; name: string };
}

export interface RepostRunInput {
  usernames: string[];
  createFolder?: boolean;
  folderName?: string;
  targetCount?: number;
  excludeOfficial?: boolean;
  /** Keep only person-led accounts, or only faceless ones. */
  presenterFilter?: 'faces' | 'faceless';
}

export interface RepostRunResult {
  jobId: string;
  folder: { publicId: string; name: string; isNew: boolean };
}

// ── workspace (agent A) ──

export type FolderStatus = 'active' | 'complete' | 'archived';

/** Per-folder lead counts by lifecycle bucket (contract §3). */
export interface FolderCounts {
  total: number;
  notSent: number;
  inQueue: number;
  messaged: number;
  logged: number;
  done: number;
  irrelevant: number;
  open: number;
}

export type LeadDoneReason =
  | 'logged'
  | 'joined'
  | 'dismissed'
  | 'suppressed'
  | 'irrelevant'
  | 'complete'
  | 'wrong_fit'
  | 'messaged_no_reply';

export interface LeadAssignee {
  inviterId: number | null;
  inviterName: string | null;
  team: string | null;
}

export interface FolderRef {
  publicId: string;
  name: string;
}

export type TriState = 'any' | 'yes' | 'no';

/** Server-side filters for a folder's leads (contract §3). Also the `filter` body of bulk actions. */
export interface LeadFilter {
  q?: string;
  state?: string;
  type?: string;
  foundVia?: string;
  assignee?: string;
  minFollowers?: number;
  maxFollowers?: number;
  minEngagement?: number;
  maxEngagement?: number;
  email?: TriState;
  done?: TriState;
}

export interface LeadListQuery extends LeadFilter {
  page?: number;
  limit?: number;
  /** `followers`, `-followers`, `avgViews`, `engagement`, `added`… (`-` = descending). */
  sort?: string;
}

export interface LeadPage {
  rows: Lead[];
  total: number;
}

export type LeadMarkAction = 'irrelevant' | 'complete' | 'reopen';

/** Body for folder and creator reassignment (contract §4). */
export interface ReassignBody {
  usernames?: string[];
  creatorIds?: number[];
  all?: boolean;
  filter?: LeadFilter | CreatorListFilters;
  assignToInviterId?: number | null;
  assignToTeam?: string | null;
  releaseClaims?: boolean;
}

export interface ReassignResult {
  updated: number;
  skipped: number;
  notInOutreach: number;
}

/** Folder export body, with the contract's `all` + `filter` + `reassignExisting`. */
export interface FolderExportInput {
  leadIds?: number[];
  all?: boolean;
  filter?: LeadFilter;
  niches?: string[];
  suggestedCategory?: string;
  assignToInviterId?: number;
  assignToTeam?: string;
  reassignExisting?: boolean;
}

/**
 * Creators-screen export. NOT in the shared contract yet (additive proposal):
 * `POST /api/creators/export-to-outreach`.
 */
export interface CreatorExportInput {
  creatorIds?: number[];
  all?: boolean;
  filter?: CreatorListFilters;
  assignToInviterId?: number;
  assignToTeam?: string;
  reassignExisting?: boolean;
}

/** Assignment fields the registry may carry once the reconcile mirrors them. */
export interface CreatorAssignmentFields {
  assignedTo?: LeadAssignee | null;
  claimedByName?: string | null;
  lastContactedAt?: string | null;
}

/** Just the part of `GET /api/settings` the workspace reads. */
export interface SettingsValuesResponse {
  values: Record<string, unknown>;
  defaults?: Record<string, unknown>;
}

export type GenerationOutcome = 'idle' | 'running' | 'created' | 'empty' | 'failed';

export interface GenerationSkippedSource {
  code?: string;
  source?: string;
  label?: string;
  reason?: string;
}

/** `GET /api/search-suggestions/generation` with the outcome fields. */
export interface GenerationStatus extends Omit<GenerationRun, 'result'> {
  outcome?: GenerationOutcome;
  message?: string | null;
  progress?: { sourcesTotal: number; sourcesDone: number; current: string | null } | null;
  serverStartedAt?: string | null;
  result:
    | (Omit<GenerateResult, 'sourcesSkipped'> & {
        sourcesSkipped?: GenerationSkippedSource[];
        emptyReason?: string | null;
        emptyReasonCode?: string | null;
      })
    | null;
}

// ── search (agent B) ──
// Shapes from DISCOVERY_REBUILD_CONTRACT.md §1 (settings), §2 (search schema +
// saved searches) and §5 (overlap + near misses). Fields marked "client
// extension" are optional and additive: the server may omit them and the UI
// falls back to built-in rules (see src/discovery/search/params.ts).

export type SearchFilterType =
  | 'integer'
  | 'number'
  | 'percent'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'countryList'
  | 'languageList'
  | 'text';

export interface SearchFilterOption {
  value: string | number;
  label: string;
}

export interface SearchFilterDef {
  /** The ScrapeParams key this filter writes. */
  key: string;
  label: string;
  /** Id of a `groups` entry. */
  group: string;
  type: SearchFilterType;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  unit?: string | null;
  /** Null/absent = every platform. */
  platforms?: Platform[] | null;
  /** Null/absent = every mode. */
  modes?: string[] | null;
  help?: string | null;
  costHint?: string | null;
  placeholder?: string | null;
  options?: SearchFilterOption[];
  /** Client extension: only shown and sent while this other filter is set. */
  dependsOn?: string | null;
  /** Client extension: hidden (and not sent) while this other filter is on. */
  hiddenWhen?: string | null;
  /** Client extension, `text` only: sent as a string[] split on commas/newlines. */
  list?: boolean;
}

export interface SearchModeDef {
  id: string;
  label: string;
  queryLabel: string;
  queryPlaceholder?: string | null;
  /** One entry per line, each run as its own search. */
  multi: boolean;
  /** Client extension: what this mode is good for. */
  hint?: string | null;
}

export interface SearchPlatformDef {
  id: Platform;
  label: string;
  available: boolean;
  modes: SearchModeDef[];
}

export interface SearchGroupDef {
  id: string;
  label: string;
  defaultOpen: boolean;
}

export type SearchParamValue = string | number | boolean | string[] | null;

export interface SearchSchema {
  platforms: SearchPlatformDef[];
  filters: SearchFilterDef[];
  groups: SearchGroupDef[];
  defaults: Record<string, SearchParamValue>;
}

/** `GET /api/search-presets`, normalised (contract §2). */
export interface SavedSearch {
  id: string;
  name: string;
  platform: Platform | null;
  params: Record<string, unknown>;
  createdByName: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/** 400 `{ error, fieldErrors }` from validated endpoints. */
export type FieldErrors = Record<string, string>;

export type SettingType = 'integer' | 'number' | 'percent' | 'boolean' | 'string' | 'stringList';
export type SettingValue = number | boolean | string | string[] | null;

export interface SettingField {
  key: string;
  label: string;
  group: string;
  type: SettingType;
  min?: number | null;
  max?: number | null;
  help?: string | null;
}

export interface SettingsResponse {
  values: Record<string, SettingValue>;
  defaults: Record<string, SettingValue>;
  fields: SettingField[];
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface SettingsAuditEntry {
  key: string;
  oldValue: unknown;
  newValue: unknown;
  changedByName: string | null;
  changedAt: string;
}

export interface OverlapFolderRef {
  publicId: string;
  name: string;
}

export interface OverlapCreator {
  username: string;
  platform?: Platform | string | null;
  followers: number | null;
  avgViews: number | null;
  folders: OverlapFolderRef[];
  outreachState: OutreachState;
  assignedToName: string | null;
  claimedByName: string | null;
  included: boolean;
}

export interface JobOverlap {
  total: number;
  byFolder: { folderPublicId: string; name: string; count: number }[];
  creators: OverlapCreator[];
}

export interface NearMissChange {
  filter: string;
  from: number | null;
  to: number;
}

export interface NearMissCreator {
  username: string;
  platform?: Platform | string | null;
  followers: number | null;
  avgViews: number | null;
  /** The value that missed the threshold (for a combination: the first change's filter). */
  value: number | null;
}

export interface NearMissOption {
  id: string;
  changes: NearMissChange[];
  count: number;
  creators: NearMissCreator[];
}

export interface NearMisses {
  total: number;
  options: NearMissOption[];
}

/** What job poll / job result responses gain in contract §5. */
export interface ScrapeJobInsights {
  overlap?: JobOverlap | null;
  nearMisses?: NearMisses | null;
  /** Legacy name of `widenAnalysis`; read only when `nearMisses` is absent. */
  widen?: WidenOption[] | null;
}

export type SearchJobPoll = ScrapeJobPoll & ScrapeJobInsights;
export type SearchJobResult = ScrapeJob & ScrapeJobInsights & { widenAnalysis?: WidenOption[] | null; params?: Record<string, unknown> };

export type IncludeSource = 'overlap' | 'nearMiss';
